/**
 * Category Cockpit — API client (beta, super-admin only).
 *
 * Read-only view of the category knowledge graph, per sector. The sector comes from the
 * selected site's sectors (sites.sector_id / sector_ids), never from a selector of its own.
 *
 * Backend: backend-merged/routes/category-cockpit.js (new, read replica) and, for the content
 * of the 10 aspects, the existing backend-merged/routes/category-characteristics.js GET.
 * Plan:    docs/reports/20260922-1056-category-cockpit-admin-integration-plan.md
 */

import { authFetch } from '@/utils/auth-fetch';

const BASE_URL = '/api/category-cockpit';
const CHARACTERISTICS_URL = '/api/category-characteristics';

// ============================================================================
// Types — mirror the service contract exactly (snake_case, as the backend renders it)
// ============================================================================

export type AspectState = 'fresh' | 'stale' | 'empty' | 'never' | 'error';
export type Dot = 'f' | 'h' | '';
export type ActionState = 'blocked' | 'attention' | 'todo' | 'ready' | 'off';
export type Queue = 'action' | 'ready' | 'research';
export type ChapterId =
  | 'sector' | 'identity' | 'aspects' | 'schema' | 'contrast' | 'outcomes' | 'readiness'
  | 'demand' | 'intent' | 'market' | 'news' | 'docs' | 'experts' | 'relations';

export const ASPECTS = [
  'product_types', 'personas', 'use_cases', 'brand_tiers', 'spec_semantics',
  'validation_rules', 'premium_features', 'price_tiers', 'measurement_units', 'tier_vocabulary',
] as const;
export type Aspect = typeof ASPECTS[number];

export interface GapCount { type: string; status: string; n: number }

export interface SiteCell {
  site_id: string;
  site_name: string | null;
  category_path: string;
  products: number;
  enriched: number;
  products_in_children: number;
  spec_hints: number;
  spec_hints_stale: number;
  hints_updated: string | null;
  pages_published: number;
  gaps: GapCount[];
  tier: string | null;
  is_ready?: boolean;
  l1_score?: number;
  l3_score?: number;
  l5_score?: number;
  overall_score?: number;
  scored_at?: string;
  l1_product_types_count?: number;
  l3_products_total?: number;
  l3_products_below_threshold?: number;
  l5_canonical_attrs_count?: number;
  canonical_attrs?: number;
  canonical_generation_id?: string | null;
  l5_hints_populated?: number;
  gap_details?: Record<string, unknown> | null;
}

export interface OutcomeTotals {
  total: number; last30: number; published: number; needs_review: number; draft: number;
  approved: number; pending_review: number; rejected: number; archived: number;
}

export interface BlastSide {
  sites: number; sectors: number; products: number; enriched: number; pages: number;
  others: Array<{ site_id: string; site_name: string; sector_id: number; sector: string | null; products: number }>;
}

export interface NextAction { k: ActionState; t: string; why: string; ch: ChapterId | 'inbox'; none?: boolean }

export interface Derived {
  states: Record<AspectState, number>;
  aspects: Array<{ a: Aspect; state: AspectState; n: number }>;
  dots: [Dot, Dot, Dot, Dot];
  next_action: NextAction;
  flags: Array<['' | 'warn' | 'bad', string]>;
  attention: number;
  queues: Queue[];
  chapters: Record<ChapterId, { status: [string, string, string?]; ladder: [Dot, Dot, Dot, Dot] }>;
}

export interface CategoryRow {
  /**
   * `category_characteristics.id`. It travels because two write endpoints are keyed on the id and
   * not on the path — the extraction-hints route resolves the category by id, and the per-aspect
   * refresh compares `updated_at` against the row THIS payload described. A screen that re-looked
   * either of them up would be acting on a row it never showed.
   *
   * A uuid, like `sector_category_templates.id` which the hints route tries first — so the same
   * value is safe to send to a route that looks in both tables (measured on db2, 2026-09-23).
   */
  id: string;
  category_path: string;
  category_name: string | null;
  status: string | null;
  research_error: string | null;
  research_source: string | null;
  research_model: string | null;
  last_researched_at: string | null;
  stale_after: string | null;
  research_cost_usd: number | null;
  sample_product_count: number | null;
  times_used: number | null;
  last_used_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  canonical_attrs: number;
  perceived_attrs: number;
  questions: number;
  history_runs: number | null;
  sectors_with_this_path: number;
  split_proposals: number;
  domain_knowledge_active: number;
  sites: SiteCell[];
  outcome_totals: OutcomeTotals;
  blast: { sector: BlastSide; global: BlastSide };
  spec_sites_n: number;
  decisions_open: number;
  derived: Derived;
  // the ten `<aspect>_state` / `<aspect>_n` columns
  [key: string]: unknown;
}

export interface SectorSite { id: string; name: string; domain: string | null; assigned_to_sector: boolean; readiness_rows: number }

export interface Totals {
  categories: number; sites: number; ready_pairs: number; scored_pairs: number; stale_cells: number; never_cells: number; error_cells: number;
  stale_categories: number; never_categories: number; gaps_open: number; products: number; enriched: number;
  pages_published: number; pages_in_review: number; pages_rejected: number; queues: Record<Queue, number>;
}

export interface Meta { source: string; computed_ms: number; list_cached?: boolean }

export interface CategoryList {
  sector: { id: number; name: string };
  sites: SectorSite[];
  categories: CategoryRow[];
  totals: Totals;
  history_table: boolean;
  computed_at: string;
  cached: boolean;
  _meta: Meta;
}

export interface SiteSectors {
  site: { id: string; name: string };
  primary_sector_id: number | null;
  sectors: Array<{ id: number; name: string | null }>;
}

export interface OutcomeSiteRow { site_id: string; site_name: string; total: number; last30: number; last: string | null; [status: string]: unknown }

export interface CategoryDetail {
  sector: { id: number; name: string };
  sites: SectorSite[];
  category: CategoryRow;
  children: Array<{ category_path: string; derived: { dots: Derived['dots']; next_action: NextAction }; sites: Array<{ site_name: string | null; products: number; tier: string | null; is_ready?: boolean }> }>;
  siblings: string[];
  other_sectors_same_path: Array<{ sector_id: number; sector: string; last_researched_at: string | null; sample_product_count: number | null; research_source: string | null }>;
  outcomes: OutcomeSiteRow[];
  outcomes_subtree: Array<{ category_path: string; status: string; n: number }>;
  review_notes: Array<{ status: string; category_path: string; reason: string; day: string; site_name: string }>;
  blast_rows: Array<{ site_id: string; site_name: string; sector_id: number; sector: string | null; category_path: string; products: number; enriched: number }>;
  spec_sites: Array<{ site_id: string; site_name: string; sector_id: number; category_path: string; hints: number; active: number; stale: number; updated: string | null; created: string | null; avg_conf: number | null }>;
  readiness_series: Array<{ site_id: string; day: string; tier: string; overall_score: number; is_ready: boolean; l1_score: number; l3_score: number; l5_score: number }>;
  research_history: Array<{ research_type: string; model: string | null; cost_usd: number | null; started_at: string | null; duration_ms: number | null; success: boolean | null; error_message: string | null; sample_product_count: number | null }> | null;
  history_table: boolean;
  knowledge_notes: Array<{ id: string; scope_level: string; scope_attribute_name: string | null; title: string; knowledge_type: string | null; status: string; created: string; times_used_in_generation: number | null; site_id: string | null }>;
  perceived_attributes: Array<{ attribute_name: string; benefit_layer: string | null; purchase_driver_strength: number | null; is_validated: boolean | null }>;
  demand: { topics: Array<{ topic_name: string; topic_type: string; avg_monthly_searches: number; trend_yoy_pct: number | null; cpc_avg: number | null }>; meta: { sector_topics: number; sector_taxonomy_rows: number } | null };
  sector_context: {
    personas: Array<{ persona_name: string; archetype: string | null; age_range: string | null; gender: string | null; occupation: string | null; description: string | null; is_active: boolean; research_date: string | null; research_model: string | null; canonical: boolean }>;
    topics: Array<{ topic_type: string; n: number; with_volume: number; last: string | null }>;
    sites: Array<{ id: string; name: string; primary_sector: boolean; products: number; readiness_rows: number; ready: number }>;
  };
  /** the sector chapter: the 8 largest categories of the sector by products, and the sector's totals */
  sector_top: Array<{ category_path: string; products: number; best_site: SiteCell | null; states: Record<AspectState, number>; last_researched_at: string | null }>;
  sector_totals: Totals;
  computed_at: string;
  _meta: Meta;
}

export interface SchemaDetail {
  canonical_attributes: Array<{ attribute_name: string; canonical_name: string | null; data_type: string | null; unit: string | null; typical_min: number | null; typical_max: number | null; enum_values: unknown; is_key_attribute: boolean | null; priority_rank: number | null; source: string | null; description: string | null; has_provenance: boolean; created: string | null }>;
  canonical_by_site?: Array<{ site_id: string; category_path: string; generation_id: string | null; attributes: SchemaDetail['canonical_attributes'] }>;
  spec_hints: Array<{ site_id: string; site_name: string; spec_name: string; spec_display_name: string | null; confidence_score: number | null; times_used: number | null; times_overridden: number | null; status: string; stale_after: string | null; updated: string | null }>;
  truncated?: Truncation[];
  /** rows in attribute_value_ranges for this sector and path; null = the table is absent, NOT zero */
  observed_ranges?: number | null;
  /**
   * Extracted-attribute counts per site. `top` marks the 40 most frequent keys — the ranked
   * list the schema chapter displays. Rows with `top: false` are canonical attributes that
   * fall outside that ranking and are returned anyway, so looking a key up by name gives its
   * true count instead of an absence that reads as zero coverage.
   */
  extraction_coverage: Array<{ site_id: string; key: string; n: number; spellings?: string[]; top?: boolean }>;
  enriched_by_site: Array<{ site_id: string; enriched: number; products: number }>;
  knowledge_attributes: Array<{ scope_level: string; scope_attribute_name: string | null; title: string; status: string; created: string }>;
  gaps_by_attribute: Array<{ attribute_name: string; gap_type: string; status: string; n: number; products: number | null }>;
  computed_at: string;
  cached: boolean;
  _meta: Meta;
}

export interface ContrastDetail {
  gaps_by_attribute: SchemaDetail['gaps_by_attribute'];
  gap_examples: Array<{ id: string; gap_type: string; status: string; attribute_name: string; technology_value: string | null; suggested_semantic_value: string | null; final_semantic_value?: string | null; suggestion_confidence: number | null; product_count: number | null; suggested: string | null; site_id: string }>;
  computed_at: string;
  _meta: Meta;
}

export type DecisionGroup = 'reenrich' | 'unappliable' | 'expert' | 'suggested' | 'pending' | 'split' | 'keymatch' | 'errors' | 'regen';

/**
 * The entity one question can be answered against, when it HAS one.
 *
 * The backend names the entity, never the endpoint: a client that derived the row from a display
 * string (`id: 'aa:<uuid>'`, a site name, a leaf label) would act on the wrong row the first time
 * one of those strings changed. A question without `act` is a deliberate absence — it is answered
 * by a review session on the page `link` points to, not by one call (categoryDerive.js).
 */
export type DecisionAct =
  /**
   * `effect` and `spends` are read from the handler's own code by the backend
   * (categoryDerive `AGENT_ACTIONS`). An action_type the backend has not read carries no
   * descriptor at all, so this union is only ever built for handlers whose effect is known.
   */
  /** `draft` marks a knowledge draft filed by this cockpit (band D): approve / reject a person's proposed change */
  | { kind: 'agent_action'; id: string; action_type: string; destructive: boolean; spends: boolean; effect: string; draft?: boolean }
  /**
   * `n` is the appliable count IN THE SELECTED SECTOR; `n_all_sectors` is the same count with no
   * sector predicate, which is the scope the endpoint actually writes. `null` means it was not
   * measured — distinct from "the same", and the confirmation says which.
   */
  | { kind: 'apply_gaps'; site_id: string; n: number; n_all_sectors: number | null; sectors: number | null }
  | { kind: 'split'; id: string; site_id: string; children: number | null }
  | { kind: 'key_match'; id: number; proposed_key: string }
  | { kind: 'key_match_reject_only'; id: number }
  | { kind: 'research_retry'; path: string };

export interface Decision {
  id: string;
  act?: DecisionAct;
  group: DecisionGroup;
  real: boolean;
  link: string;
  labels: [string, string];
  site: string;
  site_id?: string;
  path?: string;
  since: string | null;
  q: string;
  ev: Array<[string, string | number]>;
  impact: string;
  risk: 'hi' | 'lo' | '';
  who: string;
  unblocks: string;
  resumes: string;
  warn?: string;
  tbl: string;
}

export interface Decisions {
  /** decision queues that stop at their cap: shown vs total matching */
  truncated: Truncation[];
  sector: { id: number; name: string };
  questions: Decision[];
  groups: Record<DecisionGroup, string>;
  sources: Record<string, unknown[]>;
  computed_at: string;
  cached: boolean;
  _meta: Meta;
}

export type JournalKind = 'gaps' | 'proposal' | 'journal' | 'schema' | 'research' | 'hints' | 'readiness' | 'pages';

export interface JournalEntry {
  at: string; kind: JournalKind; scope: string; what: string; who: string; how: string; tbl: string; rev: [string, string]; detail: string;
}

/** a source that came back at its cap: what it is, and where the cap sits */
export interface Truncation { source: string; limit: number; shown?: number; total?: number }

export interface Journal {
  sector: { id: number; name: string };
  /** the category this journal was restricted to, or null for the whole sector */
  path: string | null;
  /** sources that hit their row cap, so the history shown may be incomplete */
  truncated: Truncation[];
  entries: JournalEntry[];
  kinds: Record<JournalKind, string>;
  computed_at: string;
  cached: boolean;
  _meta: Meta;
}

/** the existing category-characteristics GET, as formatted by its route (formatCharacteristicsResponse) */
export interface AspectsResponse {
  id: string;
  sectorId: number;
  categoryPath: string;
  status: string;
  aspects: Record<Aspect, { data: unknown; hasData: boolean; count?: number; tierCount?: number; specCount?: number; unitCount?: number }>;
  freshness: unknown;
  lastResearchedAt: string | null;
  staleAfter: string | null;
  researchModel: string | null;
  researchCostUsd: number | null;
  researchError: string | null;
  sampleProductCount: number | null;
  timesUsed: number;
  lastUsedAt: string | null;
}

/** A failed cockpit call. `code` is the backend's typed error code. */
export class CategoryCockpitError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'CategoryCockpitError';
    this.code = code;
  }
}

/**
 * Unwrap `{ success, data }` and surface the backend's REAL message on failure.
 * No fallback shape and no invented default: a broken read must look broken.
 */
async function unwrap<T>(res: Response): Promise<T> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    throw new CategoryCockpitError('bad_response', `HTTP ${res.status}: response was not JSON`);
  }
  if (!res.ok || !body?.success) {
    throw new CategoryCockpitError(body?.error || 'http_error', body?.message || `HTTP ${res.status}`);
  }
  return body.data as T;
}

// ============================================================================
// Calls
// ============================================================================

export async function fetchSiteSectors(siteId: string, signal?: AbortSignal): Promise<SiteSectors> {
  return unwrap<SiteSectors>(await authFetch(`${BASE_URL}/sites/${encodeURIComponent(siteId)}/sectors`, { signal }));
}

export async function fetchCategories(sectorId: number, signal?: AbortSignal): Promise<CategoryList> {
  return unwrap<CategoryList>(await authFetch(`${BASE_URL}/sectors/${sectorId}/categories`, { signal }));
}

/**
 * ⛔⛔ `fresh` IS THE DIFFERENCE BETWEEN A RE-READ AND A RE-READ THAT MEANS SOMETHING.
 * Ordinary reads are served from the replica and cached for ten minutes. Under replication lag a
 * post-write re-read therefore fetches the PRE-write row, caches it, and the screen reports a
 * successful refresh over unchanged numbers — which reads as "the write did not take", and several
 * of these writes cost money to repeat. `fresh` asks the route for the primary; it is set by the
 * post-write refresh and by nothing else, so browsing still goes to the replica.
 */
export async function fetchCategory(sectorId: number, path: string, signal?: AbortSignal, opts?: { fresh?: boolean }): Promise<CategoryDetail> {
  const fresh = opts?.fresh ? '&fresh=1' : '';
  return unwrap<CategoryDetail>(await authFetch(`${BASE_URL}/sectors/${sectorId}/category?path=${encodeURIComponent(path)}${fresh}`, { signal }));
}

/** `fresh` as in `fetchCategory`: the chapters are separate endpoints and re-read the same way. */
export async function fetchSchema(sectorId: number, path: string, signal?: AbortSignal, opts?: { fresh?: boolean }): Promise<SchemaDetail> {
  return unwrap<SchemaDetail>(await authFetch(`${BASE_URL}/sectors/${sectorId}/category/schema?path=${encodeURIComponent(path)}${opts?.fresh ? '&fresh=1' : ''}`, { signal }));
}

/** `fresh` as in `fetchCategory` — and this is the chapter that shows a rejected gap's buttons. */
export async function fetchContrast(sectorId: number, path: string, signal?: AbortSignal, opts?: { fresh?: boolean }): Promise<ContrastDetail> {
  return unwrap<ContrastDetail>(await authFetch(`${BASE_URL}/sectors/${sectorId}/category/contrast?path=${encodeURIComponent(path)}${opts?.fresh ? '&fresh=1' : ''}`, { signal }));
}

/** `fresh` as in `fetchCategory`: the post-write re-read must not be answered by a lagging replica. */
export async function fetchDecisions(sectorId: number, signal?: AbortSignal, opts?: { fresh?: boolean }): Promise<Decisions> {
  const fresh = opts?.fresh ? '?fresh=1' : '';
  return unwrap<Decisions>(await authFetch(`${BASE_URL}/sectors/${sectorId}/decisions${fresh}`, { signal }));
}

/**
 * @param path restrict the journal to ONE category. The backend filters the source rows before
 *   grouping them, so the counts describe that category; filtering the rendered text client-side
 *   both missed writes and claimed other categories' (codex dual-review on PR #6457).
 */
export async function fetchJournal(sectorId: number, path?: string, signal?: AbortSignal, opts?: { fresh?: boolean }): Promise<Journal> {
  // the path is optional here, so `fresh` is the first parameter when it is absent
  const parts = [path ? `path=${encodeURIComponent(path)}` : '', opts?.fresh ? 'fresh=1' : ''].filter(Boolean);
  const qs = parts.length ? `?${parts.join('&')}` : '';
  return unwrap<Journal>(await authFetch(`${BASE_URL}/sectors/${sectorId}/journal${qs}`, { signal }));
}

export async function dropCache(sectorId: number): Promise<{ dropped: number }> {
  return unwrap<{ dropped: number }>(await authFetch(`${BASE_URL}/sectors/${sectorId}/cache`, { method: 'DELETE' }));
}

/**
 * The content of the 10 aspects, from the route that already serves the Category Research page.
 * `data` is null when the row does not exist (the route answers success with data null).
 */
export async function fetchAspects(sectorId: number, path: string, signal?: AbortSignal): Promise<AspectsResponse | null> {
  return unwrap<AspectsResponse | null>(await authFetch(`${CHARACTERISTICS_URL}/${sectorId}/category/${encodeURIComponent(path)}`, { signal }));
}

/**
 * Canonical names of the benefits a persona weights (`personas[].benefit_weights` is keyed by
 * benefit_canonical.id). `unknown` lists the ids with no benefit_canonical row: an id the reader
 * cannot name is reported as such, never dropped. The route caps one read at 500 ids, so a larger
 * set is read in slices and merged.
 */
export interface BenefitNames {
  names: Record<string, { name: string; status: string }>;
  unknown: string[];
}
const BENEFIT_IDS_PER_READ = 500;

export async function fetchBenefitNames(ids: string[], signal?: AbortSignal): Promise<BenefitNames> {
  const out: BenefitNames = { names: {}, unknown: [] };
  const unique = [...new Set(ids.map((id) => id.toLowerCase()))];
  for (let i = 0; i < unique.length; i += BENEFIT_IDS_PER_READ) {
    const slice = unique.slice(i, i + BENEFIT_IDS_PER_READ);
    const r = await unwrap<BenefitNames>(await authFetch(`${BASE_URL}/benefits?ids=${slice.join(',')}`, { signal }));
    Object.assign(out.names, r.names);
    out.unknown.push(...r.unknown);
  }
  return out;
}

// ============================================================================
// The aspect judge (band C) — the ONLY model opinion in the Category Cockpit.
//
// Rendered in VIOLET, as in the product cockpit: neither verbatim database content nor a
// deterministic computation. `discarded_count` is part of the contract — findings the model made
// outside its recipe are dropped by validation AND counted, and a filter the reader cannot see is
// a filter the reader cannot trust.
// ============================================================================

/** `indeterminate` is not a fourth severity: the judge asserted a gap nothing surviving validation supports. */
export type AspectJudgeVerdict = 'complete' | 'minor_gaps' | 'major_gaps' | 'indeterminate';

export interface AspectJudgeSection {
  verdict: AspectJudgeVerdict;
  /** present when the verdict was downgraded to indeterminate, saying why */
  verdict_note?: string;
  missing: Array<{ element: string; severity: 'major' | 'minor'; evidence: string }>;
  weak: Array<{ element: string; why: string; evidence: string }>;
  note: string;
}

export interface AspectJudgement {
  id: string;
  sector_id: number;
  category_path: string;
  aspect: Aspect;
  model: string;
  /** keyed by aspect — exactly one entry */
  verdicts: Record<string, AspectJudgeSection>;
  summary: {
    sections_judged: number; major_gaps: number; minor_gaps: number; indeterminate?: number;
    /** the aspect was longer than the judge's budget: it read `shown` of `total` entries */
    content_truncated?: { shown: number; total: number } | null;
    /** no product of the sector's sites is filed under this category — the catalogue proved nothing */
    catalogue_empty?: boolean;
    /** false when the category's prices span currencies (or carry none) — no price was compared across them */
    price_comparable?: boolean | null;
  } | null;
  discarded_count: number;
  recipes_version: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
  created_at: string;
  /** true when the stored verdict was computed over DIFFERENT evidence than today's; null = unknown */
  stale?: boolean | null;
  staleness_known?: boolean;
  /** true when unchanged evidence returned the stored verdict instead of paying again */
  reused?: boolean;
}

/**
 * One judge run as the server reports it. The model call outlives the edge timeout, so a POST
 * answers `running` and the screen polls the run until it is `complete` (a verdict) or `failed`.
 */
export type AspectJudgeRun =
  | { id: string; status: 'running'; created_at: string; joined?: boolean }
  | (AspectJudgement & { status: 'complete' })
  | (AspectJudgeFailure & { status: 'failed' });

/** A run that failed, or was abandoned past the reservation window — it may still have been billed. */
export interface AspectJudgeFailure {
  id: string;
  created_at: string;
  completed_at: string | null;
  abandoned: boolean;
  error: string | null;
  input_tokens: number | null;
  output_tokens: number | null;
  cost_usd: number | null;
}

/**
 * What the judge knows about one aspect: the newest verdict, the run still in flight, and the
 * newest failed attempt when nothing has superseded it.
 */
export interface AspectJudgeState {
  judgement: AspectJudgement | null;
  in_flight: { id: string; created_at: string } | null;
  last_failure: AspectJudgeFailure | null;
}

const judgeUrl = (sectorId: number, aspect: Aspect, verb: string, path: string) =>
  `${BASE_URL}/sectors/${sectorId}/category/aspects/${encodeURIComponent(aspect)}/${verb}?path=${encodeURIComponent(path)}`;

/** The stored judgement of one aspect and any run in flight. Free — reads rows, never calls a model. */
export async function fetchAspectJudgement(sectorId: number, path: string, aspect: Aspect, signal?: AbortSignal): Promise<AspectJudgeState> {
  return unwrap<AspectJudgeState>(await authFetch(judgeUrl(sectorId, aspect, 'judgement', path), { signal }));
}

/**
 * Start the judge on one aspect. A POST because it spends money and stores a row — nothing is
 * judged unless a human asks. Capped per sector per day, and idempotent on the evidence: unchanged
 * evidence answers the stored verdict at once, a run already in flight is joined.
 */
export async function runAspectJudge(sectorId: number, path: string, aspect: Aspect, opts: { force?: boolean } = {}): Promise<AspectJudgeRun> {
  return unwrap<AspectJudgeRun>(await authFetch(judgeUrl(sectorId, aspect, 'judge', path), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts),
  }));
}

/** One run, polled after a start until it settles. */
export async function fetchAspectJudgeRun(sectorId: number, path: string, aspect: Aspect, runId: string, signal?: AbortSignal): Promise<AspectJudgeRun> {
  return unwrap<AspectJudgeRun>(await authFetch(judgeUrl(sectorId, aspect, `runs/${encodeURIComponent(runId)}`, path), { signal }));
}
